"use client";
import React, { useEffect, useState } from "react";
import Breadcrumb, { type BreadcrumbPath } from "@/app/components/breadcrumb";
import { useAuth } from "@/context/authcontext";
import ICMWrapper from "@/app/components/icm-wrapper";
import PageHeroSection from "@/app/components/shared/page-hero-section";
import Testimonial from "@/app/components/testimonial";
import WhyICM from "@/app/components/why-icm";
import FdGraph from "@/app/alts/fd/components/fd-graph";
import HowToComponent from "@/app/components/shared/how-to-component";
import { useQuery } from "@tanstack/react-query";
import FDLisitingCards from "@/app/alts/fd/components/fd-listing-cards";
import { getHighestReturns } from "@/app/utils/helper";
import GetICMAppModal from "@/app/components/get-icm-app-modal";
import FdAppDrawer from "@/app/alts/fd/components/fd-app-drawer";
import FdCalculator from "@/app/alts/fd/components/fd-calculator";
import Faq from "@/app/components/faq";
import { HOW_TO_FD_STEPS, WHY_ICM_FD } from "@/app/constants/fd-data";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { getFDListData } from "@/app/api/service/fixed-deposits";
// import { FCM } from "../interface/homepage";
// import FCMInit from "../components/fcm-init";
// import { AnnouncementDialog } from "../components/popup/announcement";
// import { ANNOUNCEMENT_TRIGGER_SCREENS } from "../utils/constants";
import { useFcmToken } from "@/context/fcmcontext";
import { saveFcmToken } from "@/app/api/service/firebase";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import ReadonlyAuthRedirect from "@/app/components/readonly/readonly-auth-redirect";
import { DirectPaths } from "@/config/routes";

const Page = () => {
    const { token } = useFcmToken();
    const { isAuthenticated } = useAuth();
    const [highestReturns, setHighestReturns] = useState<number>();
    const [showAppModal, setShowAppModal] = useState<boolean>(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const { cleverTapTrackEvent } = useCleverTapEvents();

    // const [message, setmessage] = useState<FCM>();
    // const [renderAnnouncement, setrenderAnnouncement] = useState<boolean>(false);

    useEffect(() => {
        if (token) {
            saveFcmToken(token);
        }
    }, [token]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    // useEffect(() => {
    //     if (message) {
    //         setrenderAnnouncement(true);
    //     }
    // }, [message]);

    const {
        data: fdData,
        isFetched,
        isLoading,
    } = useQuery({
        queryKey: ["fdListData"],
        queryFn: getFDListData,
    });

    useEffect(() => {
        const highestReturns = getHighestReturns(fdData?.data);
        setHighestReturns(highestReturns);
    }, [fdData]);

    const toggleDownloadApp = (event?: string) => {
        if (event) {
            cleverTapTrackEvent(event, {});
            // initBranch()
            //     .then(() => {
            //         trackEvent(event, "", "");
            //     })
            //     .catch((error) => console.error("Error initializing Branch:", error));
            setAnalyticsEvent(window.location.origin, event);
        }

        setShowAppModal(!showAppModal);
        setIsDrawerOpen(!isDrawerOpen);
    };

    // Error state when data fetch fails
    if (!isFetched && !isLoading) {
        return (
            <div id="error-state" className="flex h-screen w-full items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-brand-primary-500">
                        Unable to load FD data
                    </h2>
                    <p className="mt-2 text-gray-600">Please refresh the page or try again later</p>
                </div>
            </div>
        );
    }

    // Render actual content when data is available
    return (
        <>
            {/* <FCMInit setMessage={setmessage} />
            {message?.triggerScreen === ANNOUNCEMENT_TRIGGER_SCREENS.FDLandingScreen && (
                <>
                    <AnnouncementDialog
                        renderAnnouncement={renderAnnouncement}
                        setrenderAnnouncement={setrenderAnnouncement}
                        data={message}
                    />
                </>
            )} */}
            <ReadonlyAuthRedirect
                redirectPreLogin={String(process.env.ICM_FD_URL + DirectPaths.FD_POSTFIX_PATH)}
                redirectPostLogin={String(process.env.ICM_FD_POST_URL + DirectPaths.FD)}
            >
                <ICMWrapper>
                    <Breadcrumb
                        paths={
                            [
                                { label: "Home", route: isAuthenticated ? "/platform" : "/" },
                                { label: "FDs" },
                            ] as BreadcrumbPath[]
                        }
                    />
                </ICMWrapper>
                <div id="fd-page" className="flex w-full flex-col">
                    <PageHeroSection
                        label1="High returns, maximum security!"
                        label2={`Earn upto ${highestReturns ? highestReturns?.toFixed(2) : 0}% annually`}
                        label3="Invest with confidence in FDs insured by DICGC insured upto ₹5 lakh"
                        textCarousel={[
                            "Regulated by Reserve Bank of India (RBI).",
                            "No bank account needed",
                            "Start with low ticket size",
                        ]}
                        isLoading={!highestReturns}
                        toggleDownloadApp={toggleDownloadApp}
                    />

                    <FDLisitingCards
                        fdData={fdData?.data}
                        isFDLoading={isLoading}
                        isFdFetched={isFetched}
                    />

                    <FdGraph
                        toggleDownloadApp={toggleDownloadApp}
                        highestReturns={highestReturns ? highestReturns : 0}
                        isLoading={isLoading}
                    />

                    <HowToComponent
                        title={"How to book FDs"}
                        steps={HOW_TO_FD_STEPS}
                        buttons={[
                            {
                                label: "Book Now",
                                variant: "primary",
                                onClick: toggleDownloadApp, // Pass the function reference, not the result
                            },
                        ]}
                    />

                    <FdCalculator toggleDownloadApp={toggleDownloadApp} renderHeader={true} />

                    <div
                        id="why-icm-section"
                        className="bg-neutral-50 px-s9 pb-s15 md:mb-20 md:w-[full] md:p-0"
                    >
                        <WhyICM
                            title="Why invest through <br/>InCred Money?"
                            data={WHY_ICM_FD}
                            page="fd"
                            classNames="md:my-s20"
                        />
                    </div>

                    <Testimonial toggleDownloadApp={toggleDownloadApp} sectionClassNames="px-6" />
                    <GetICMAppModal open={showAppModal} onClose={toggleDownloadApp} />
                    <FdAppDrawer isOpen={isDrawerOpen} onClose={toggleDownloadApp} />

                    <Faq
                        bgColor="bg-white"
                        compact={false}
                        product="fd"
                        subProduct="generic"
                    />
                </div>
            </ReadonlyAuthRedirect>
        </>
    );
};

export default Page;
